/**
 * RealEngineManager — Webapp-only orchestrator for workspace lifecycle,
 * identity management, registry sync, and MCP connections.
 *
 * Extracted from realWorkspace.ts during SDK migration (Batch 7).
 * Implements the EngineManager interface (Spec §5).
 */
import type {
    EngineManager,
    EngineManagerEventPayload,
    EngineManagerEventType,
    IdentityProvider,
    McpConnectionEntry,
    ProfileEntry,
    Workspace,
    WorkspaceEntry,
    WorkspaceInfo,
    UserIdentity,
} from './types'
import { RealWorkspace, createRealWorkspace, EventHub } from './realWorkspace'
import { HttpRemoteStore } from './providers'
import { WsNotificationClient } from './wsNotificationClient'
import { getCrossTabChannel } from './crossTabChannel'
import { IdbIdentityVaultStore, MemoryIdentityVaultStore, VaultIdentityProvider, IdbTrustedDeviceStore, MemoryTrustedDeviceStore } from './identityVault'
import { ConfigBlobSync, deriveRegistryKeys } from './configBlobSync'
import { registryMerge, type RegistryState, type RegistryWorkspaceEntry } from './registryMerge'
import { saveActiveWorkspace, clearActiveWorkspace } from './parseUrl'
import { API_BASE_URL, getTrustedDeviceApiBaseUrl } from '../config'
import { refreshConfigBlobOwner, repushConfigBlobIfMissing } from './mcp/configBlobService'
import { getConfigReaderSeed } from './mcp/crypto'
import { buildUpdatedConfig, metadataFromConnection } from './mcp/client'
import { buildEditPayload, type RegistryEntry } from './mcp/connectionEditing'
import { getWriteSeed } from './mcp/utils'
import { getExplicitLocalWorkspaceSyncPolicy, getLocalWorkspaceSyncPolicy } from './workspaceSyncPolicy'

function stripLocalRegistryFields(entry: RegistryWorkspaceEntry & { workspaceSyncMode?: unknown }): RegistryWorkspaceEntry {
    const sanitized = { ...entry }
    delete (sanitized as { workspaceSyncMode?: unknown }).workspaceSyncMode
    return sanitized
}

export class RealEngineManager implements EngineManager {
    private readonly events = new EventHub<EngineManagerEventPayload>()
    private readonly registry = new Map<string, RegistryWorkspaceEntry>()
    private readonly mcpConnections = new Map<string, McpConnectionEntry>()
    private registryPreferences: Record<string, unknown> = {}
    private readonly workspaces = new Map<string, Workspace>()
    private readonly syncStateUnsubscribes = new Map<string, () => void>()
    private readonly sqlite3mtHeadSubscriptions = new Map<string, Set<string>>()
    private readonly wsClients = new Map<string, WsNotificationClient>()
    private readonly suspendedWorkspaceIds: string[] = []
    private activeWorkspace: Workspace | null = null
    private registrySync: ConfigBlobSync<RegistryState> | null = null
    private registrySyncTimer: ReturnType<typeof setTimeout> | null = null
    private registryPollTimer: ReturnType<typeof setInterval> | null = null
    private registryBackoffMs = 0
    /** §5.9: Semantic timestamp — only updated on actual local mutations (saveRegistry) */
    private registryUpdatedAt = ''
    private static readonly REGISTRY_SYNC_DEBOUNCE_MS = 1000
    private static readonly REGISTRY_POLL_MS = 60_000
    // Lifecycle event listeners (for dispose cleanup)
    private handleVisibilityChange: (() => void) | null = null
    private handleOnlineForRegistry: (() => void) | null = null
    private handleOfflineForRegistry: (() => void) | null = null
    private handleBeforeUnload: (() => void) | null = null
    readonly identity: IdentityProvider

    constructor() {
        const vaultStore = typeof indexedDB === 'undefined' ? new MemoryIdentityVaultStore() : new IdbIdentityVaultStore()
        const trustedDeviceStore = typeof indexedDB === 'undefined' ? new MemoryTrustedDeviceStore() : new IdbTrustedDeviceStore()
        const remoteStore = new HttpRemoteStore(API_BASE_URL, '')
        this.identity = new VaultIdentityProvider(vaultStore, {
            onAutoLock: () => this.events.emit('identity:changed', undefined),
            remoteStore,
            backupApiBaseUrl: API_BASE_URL,
            trustedDeviceStore,
            trustedDeviceApiBase: getTrustedDeviceApiBaseUrl(),
        })
        this.loadRegistry()

        // T6 + T4 FIX: Cross-tab sync via BroadcastChannel
        getCrossTabChannel().onMessage((msg) => {
            if (msg.type === 'identity-changed') {
                // Another tab changed identity — reload vault and notify UI
                void (async () => {
                    const id = await this.identity.getIdentity()
                    if (!id) {
                        // Other tab logged out — close our workspaces and clear session state
                        this.teardownSession()
                    }
                    this.events.emit('identity:changed', undefined)
                })()
            } else if (msg.type === 'registry-changed') {
                // §5.10: Cross-tab: another tab changed registry and already pushed to remote.
                // Just reload from localStorage (written by the other tab's saveRegistry/applyRegistryState)
                // and notify React. Do NOT trigger syncRegistryFromRemote() — that would
                // re-broadcast registry-changed on applyRegistryState(), creating an infinite
                // feedback loop between tabs.
                this.loadRegistry()
                this.events.emit('registry:changed', undefined)
            }
        })
    }

    private get registryKey(): string {
        const identity = this.identity.getIdentity()
        return identity ? `sb:registry:${identity.kid}` : 'sb:registry'
    }

    /** Tombstone deny-list: blobIds that were permanently deleted server-side.
     *  Prevents deterministic workspaces (e.g. Home) from being re-added on reload. */
    private get tombstoneKey(): string {
        return `${this.registryKey}:tombstoned`
    }

    private getTombstonedBlobIds(): Set<string> {
        try {
            const raw = localStorage.getItem(this.tombstoneKey)
            return raw ? new Set(JSON.parse(raw)) : new Set()
        } catch { return new Set() }
    }

    private addToTombstoneList(blobId: string): void {
        const set = this.getTombstonedBlobIds()
        set.add(blobId)
        this.saveTombstones(set)
    }

    private saveTombstones(set: Set<string>): void {
        try { localStorage.setItem(this.tombstoneKey, JSON.stringify([...set])) } catch { /* quota */ }
    }

    private saveRegistry(): void {
        // §5.9: Stamp updatedAt on actual local mutation only
        this.registryUpdatedAt = new Date().toISOString()
        const data = {
            workspaces: Array.from(this.registry.values()),
            mcpConnections: Array.from(this.mcpConnections.values()),
            preferences: this.registryPreferences,
            updatedAt: this.registryUpdatedAt,
        }
        localStorage.setItem(this.registryKey, JSON.stringify(data))
        // Notify other tabs about registry change
        getCrossTabChannel().broadcast({ type: 'registry-changed' })
        // Debounce remote sync push
        this.debounceSyncRegistryPush()
    }

    /** Debounce: push local registry to remote blob after 1s of inactivity */
    private debounceSyncRegistryPush(): void {
        if (this.registrySyncTimer) clearTimeout(this.registrySyncTimer)
        console.log(`[EngineManager] debounceSyncRegistryPush scheduled (1s)`)
        this.registrySyncTimer = setTimeout(() => {
            this.registrySyncTimer = null
            void this.syncRegistryToRemote()
        }, RealEngineManager.REGISTRY_SYNC_DEBOUNCE_MS)
    }

    /**
     * Initialize the ConfigBlobSync instance and pull remote state.
     * Called after identity unlock/import/create.
     * Returns the first sync promise so callers can await it (e.g. recovery flows).
     */
    private initRegistrySync(): Promise<void> {
        // Idempotent: skip if already initialized (double-call protection)
        if (this.registrySync) return Promise.resolve()

        const identity = this.identity.getIdentity()
        if (!identity?.canonical?.primarySeed) return Promise.resolve()

        try {
            const seedBytes = this.base64UrlDecodeBytes(identity.canonical.primarySeed)
            const keys = deriveRegistryKeys(seedBytes, identity.kid)

            this.registrySync = new ConfigBlobSync(
                keys.blobId,
                keys.readKey,
                keys.writerSeed,
                keys.workspaceSecret,
                API_BASE_URL,
                registryMerge,
            )

            // First pull — returned so recovery flows can await it
            const firstSync = this.syncRegistryFromRemote()

            // §5.2 Registry polling (60s) — pause on hidden, resume on visible
            this.startRegistryPolling()

            // §5.2 Visibility-change handler
            this.handleVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    // Tab foregrounded — immediate sync + restart polling
                    void this.syncRegistryFromRemote()
                    // Also sync active workspace immediately (§7.5.1)
                    if (this.activeWorkspace) {
                        void this.activeWorkspace.syncNow().catch(err => console.warn('Visibility sync failed', err))
                    }
                    this.startRegistryPolling()
                } else {
                    // Tab backgrounded — pause registry polling (save battery)
                    this.stopRegistryPolling()
                }
            }
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', this.handleVisibilityChange)
            }

            // §5.2 Online/offline handlers for registry
            this.handleOnlineForRegistry = () => {
                void this.syncRegistryFromRemote()
                this.startRegistryPolling()
            }
            this.handleOfflineForRegistry = () => {
                this.stopRegistryPolling()
            }
            if (typeof window !== 'undefined') {
                window.addEventListener('online', this.handleOnlineForRegistry)
                window.addEventListener('offline', this.handleOfflineForRegistry)
            }

            // §5.2 beforeunload — flush pending debounce timer
            this.handleBeforeUnload = () => {
                if (this.registrySyncTimer) {
                    clearTimeout(this.registrySyncTimer)
                    this.registrySyncTimer = null
                    // Fire-and-forget — best effort on tab close
                    void this.syncRegistryToRemote()
                }
            }
            if (typeof window !== 'undefined') {
                window.addEventListener('beforeunload', this.handleBeforeUnload)
            }
            return firstSync
        } catch (e) {
            console.warn('Failed to init registry sync:', e)
            return Promise.resolve()
        }
    }

    /** Start the 60s registry polling timer */
    private startRegistryPolling(): void {
        this.stopRegistryPolling()
        // Skip when offline
        if (typeof navigator !== 'undefined' && !navigator.onLine) return
        this.registryPollTimer = setInterval(() => {
            void this.syncRegistryFromRemote()
        }, RealEngineManager.REGISTRY_POLL_MS)
    }

    /** Stop the registry polling timer */
    private stopRegistryPolling(): void {
        if (this.registryPollTimer) {
            clearInterval(this.registryPollTimer)
            this.registryPollTimer = null
        }
    }

    /** Pull remote registry, merge with local, update in-memory + localStorage */
    private async syncRegistryFromRemote(): Promise<void> {
        if (!this.registrySync) return
        const isInitial = this.registry.size === 0
        console.log(`[EngineManager] syncRegistryFromRemote (isInitial=${isInitial})`)
        this.events.emit('registry:sync-status', { state: 'syncing', isInitial })
        try {
            // Snapshot workspace set before sync for change detection
            const prevBlobIds = new Set(this.registry.keys())

            // §5.7: Pass getter so local state is read at merge-time, not sync-start
            const result = await this.registrySync.sync(() => this.buildRegistryState())

            // §4.2: Abort if identity lost during await (teardownSession nulls registrySync)
            if (!this.registrySync) return

            if (result.synced) {
                this.applyRegistryState(result.state)
                // Background: verify MCP config-blobs exist on server (data loss recovery)
                void this.verifyMcpConfigBlobs()

                // §5.6 Incoming-change detection
                for (const ws of result.state.workspaces ?? []) {
                    if (!prevBlobIds.has(ws.blobId) && !this.getTombstonedBlobIds().has(ws.blobId)) {
                        this.events.emit('registry:workspace-added', { blobId: ws.blobId, label: ws.label, emoji: ws.emoji })
                    }
                }
                for (const blobId of prevBlobIds) {
                    if (!this.registry.has(blobId)) {
                        this.events.emit('registry:workspace-removed', { blobId })
                    }
                }
            }
            // §5.5 Reset backoff on success
            this.registryBackoffMs = 0
            this.events.emit('registry:sync-status', { state: 'synced', isInitial })
        } catch (e) {
            // §5.5 Exponential backoff on failure
            this.registryBackoffMs = Math.min(
                (this.registryBackoffMs || 2000) * 2,
                60_000,
            )
            const message = e instanceof Error ? e.message : String(e)
            console.warn(`Registry sync failed (backoff ${this.registryBackoffMs}ms):`, e)
            this.events.emit('registry:sync-status', { state: 'error', isInitial, error: message })
        }
    }

    /** Push local registry state to remote blob (CAS-retry) */
    private async syncRegistryToRemote(): Promise<void> {
        if (!this.registrySync) return
        console.log(`[EngineManager] syncRegistryToRemote`)
        try {
            // §5.7: Pass getter (not snapshot) so state is read at merge-time,
            // consistent with syncRegistryFromRemote. Avoids stale label
            // capture when debounce fires after a rename.
            await this.registrySync.sync(() => this.buildRegistryState())
            if (!this.registrySync) return
        } catch (e) {
            console.warn('Registry push failed (will retry next cycle):', e)
        }
    }

    /**
     * Server data loss recovery: verify MCP config-blobs exist on server.
     * If any are missing (server lost data), rebuild and re-push from registry.
     */
    private async verifyMcpConfigBlobs(): Promise<void> {
        if (this.mcpConnections.size === 0) return
        console.log(`[EngineManager] verifyMcpConfigBlobs (${this.mcpConnections.size} connections)`)

        const identity = this.identity.getIdentity()
        if (!identity) return

        const apiBaseUrl = API_BASE_URL

        const owner = { primaryKid: identity.kid, displayName: identity.name, emoji: identity.emoji }

        for (const conn of this.mcpConnections.values()) {
            try {
                // Rebuild McpWorkspaceConfig[] from registry entries
                const registryEntries: RegistryEntry[] = Array.from(this.registry.values()).map(e => ({
                    blobId: e.blobId,
                    label: e.label,
                    emoji: e.emoji,
                    capability: e.capability,
                }))

                const selectedBlobIds = new Set(conn.workspaceBlobIds)
                const writeAccess = new Set(Object.keys(conn.mcpWriterSeeds ?? {}))
                const { configs } = buildEditPayload(
                    selectedBlobIds,
                    writeAccess,
                    registryEntries,
                    conn.mcpReaderSeeds ?? {},
                    conn.mcpWriterSeeds ?? {},
                )

                if (configs.length === 0) continue // No valid workspaces to include

                const config = buildUpdatedConfig(conn.label, configs, owner, metadataFromConnection(conn))

                await repushConfigBlobIfMissing(
                    apiBaseUrl,
                    conn.configBlobId,
                    conn.readKey,
                    getConfigReaderSeed(conn),
                    conn.writerSeed,
                    config,
                )
            } catch (e) {
                console.warn(`[MCP] Config-blob verify failed for ${conn.configBlobId.slice(0, 8)}:`, e)
            }
        }
    }

    private getMcpApiBaseUrl(connection: McpConnectionEntry): string {
        const connectionApi = connection.workspaceBlobIds
            .map(blobId => this.registry.get(blobId)?.apiBaseUrl)
            .find((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0)

        return connectionApi
            ?? Array.from(this.registry.values()).find(entry => typeof entry.apiBaseUrl === 'string' && entry.apiBaseUrl.length > 0)?.apiBaseUrl
            ?? API_BASE_URL
    }

    /** Build RegistryState from in-memory maps */
    private buildRegistryState(): RegistryState {
        const sanitizedWorkspaces = Array.from(this.registry.values()).map(entry => stripLocalRegistryFields(entry))
        return {
            v: '1',
            type: 'secureblob-registry',
            // §5.9: Reuse tracked timestamp — only saveRegistry() stamps fresh values
            updatedAt: this.registryUpdatedAt || new Date().toISOString(),
            workspaces: sanitizedWorkspaces.map(e => ({
                ...e,
                addedAt: (e as RegistryWorkspaceEntry).addedAt ?? new Date().toISOString(),
            })) as RegistryWorkspaceEntry[],
            mcpConnections: Array.from(this.mcpConnections.values()),
            preferences: { ...this.registryPreferences },
            tombstonedIds: Array.from(this.getTombstonedBlobIds()),
        }
    }

    /** Apply merged RegistryState to in-memory maps + localStorage */
    private applyRegistryState(state: RegistryState): void {
        // §5.10: Snapshot current state for change detection (prevents no-op broadcasts)
        const prevWorkspaces = JSON.stringify(Array.from(this.registry.values()))
        const prevMcpConnections = JSON.stringify(Array.from(this.mcpConnections.values()))
        const prevPreferences = JSON.stringify(this.registryPreferences)
        const prevUpdatedAt = this.registryUpdatedAt

        // §5.9: Adopt merged updatedAt so next buildRegistryState returns a stable value
        if (state.updatedAt) this.registryUpdatedAt = state.updatedAt

        // Merge tombstones: incoming tombstones are added to local ones
        const localTombstones = this.getTombstonedBlobIds()
        const incomingTombstones = state.tombstonedIds ?? []
        let tombstonesChanged = false
        for (const tid of incomingTombstones) {
            if (!localTombstones.has(tid)) {
                localTombstones.add(tid)
                tombstonesChanged = true
            }
        }
        if (tombstonesChanged) this.saveTombstones(localTombstones)

        this.registry.clear()
        this.mcpConnections.clear()
        for (const ws of state.workspaces ?? []) {
            // Respect merged tombstones
            if (localTombstones.has(ws.blobId)) continue
            const rest = stripLocalRegistryFields(ws)
            this.registry.set(rest.blobId, rest)
        }
        for (const mcp of state.mcpConnections ?? []) {
            if (localTombstones.has(mcp.configBlobId)) continue
            this.mcpConnections.set(mcp.configBlobId, mcp)
        }
        this.registryPreferences = { ...(state.preferences ?? {}) }
        // Write-through to localStorage (include updatedAt for §5.9 page-reload persistence)
        const data = {
            workspaces: Array.from(this.registry.values()),
            mcpConnections: Array.from(this.mcpConnections.values()),
            preferences: this.registryPreferences,
            updatedAt: this.registryUpdatedAt,
        }
        localStorage.setItem(this.registryKey, JSON.stringify(data))

        // §5.10: Only emit events + broadcast if state actually changed.
        // Suppressing no-op broadcasts prevents the cross-tab feedback loop:
        // Tab A broadcasts → Tab B syncs → applies (unchanged) → broadcasts → Tab A ... ∞
        const newWorkspaces = JSON.stringify(data.workspaces)
        const newMcpConnections = JSON.stringify(data.mcpConnections)
        const newPreferences = JSON.stringify(this.registryPreferences)
        const stateChanged = newWorkspaces !== prevWorkspaces
            || newMcpConnections !== prevMcpConnections
            || newPreferences !== prevPreferences
            || this.registryUpdatedAt !== prevUpdatedAt
            || tombstonesChanged
        if (stateChanged) {
            this.events.emit('registry:changed', undefined)
            getCrossTabChannel().broadcast({ type: 'registry-changed' })
        }
    }



    private base64UrlDecodeBytes(str: string): Uint8Array {
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
        while (base64.length % 4) base64 += '='
        const binary = atob(base64)
        return new Uint8Array([...binary].map(c => c.charCodeAt(0)))
    }

    private loadRegistry(): void {
        try {
            this.registry.clear()
            this.mcpConnections.clear()
            this.registryPreferences = {}
            this.registryUpdatedAt = ''
            const dataStr = localStorage.getItem(this.registryKey)
            if (dataStr) {
                const data = JSON.parse(dataStr)
                if (data.workspaces) {
                    for (const entry of data.workspaces) {
                        const rest = stripLocalRegistryFields(entry as RegistryWorkspaceEntry & { workspaceSyncMode?: unknown })
                        this.registry.set(rest.blobId, rest)
                    }
                }
                if (data.mcpConnections) {
                    for (const entry of data.mcpConnections) {
                        this.mcpConnections.set(entry.configBlobId, entry)
                    }
                }
                if (data.preferences && typeof data.preferences === 'object') {
                    this.registryPreferences = { ...data.preferences }
                }
                // §5.9: Restore updatedAt for idle no-op invariant
                if (data.updatedAt) this.registryUpdatedAt = data.updatedAt
            }
        } catch (e) {
            console.warn('Failed to load registry from localStorage', e)
        }
    }

    async openWorkspace(params: { apiBaseUrl: string; blobId: string; capability: string; canWrite?: boolean }): Promise<Workspace> {
        if (this.activeWorkspace?.blobId && this.activeWorkspace.blobId !== params.blobId) {
            this.suspendWorkspace(this.activeWorkspace.blobId)
        }

        const currentEntry = this.registry.get(params.blobId)
        const derivedCanWrite = !!getWriteSeed(params.capability)
        const nextCanWrite = params.canWrite ?? (derivedCanWrite ? true : (currentEntry?.canWrite ?? false))
        const entry = currentEntry ?? {
            blobId: params.blobId,
            label: params.blobId,
            emoji: '🗂️',
            apiBaseUrl: params.apiBaseUrl,
            capability: params.capability,
            canWrite: nextCanWrite,
        }
        const nextEntry = currentEntry
            ? { ...currentEntry, apiBaseUrl: params.apiBaseUrl, capability: params.capability, canWrite: nextCanWrite }
            : entry
        const wasKnown = this.registry.has(params.blobId)
        this.registry.set(nextEntry.blobId, nextEntry)

        const existing = this.workspaces.get(params.blobId)
        if (existing) {
            if (this.shouldReloadWorkspace(existing, nextEntry)) {
                this.unloadWorkspace(params.blobId, existing)
            } else {
                this.syncLoadedWorkspaceMetadata(params.blobId, nextEntry)
                this.setActiveWorkspace(existing)
                if (!wasKnown || this.workspaceEntryChanged(currentEntry, nextEntry)) {
                    this.saveRegistry()
                    this.events.emit('registry:changed', undefined)
                }
                return existing
            }
        }

        const identity = this.identity.getIdentity()
        const kid = identity?.kid ?? 'guest'
        const workspace = createRealWorkspace(nextEntry, kid, undefined, this.identity)
        const initialSyncPolicy = getLocalWorkspaceSyncPolicy(params.blobId)
        if (initialSyncPolicy === 'paused') {
            ;(workspace as RealWorkspace).setSyncPolicy('paused')
        }
        await (workspace as RealWorkspace).initialize()
        if (initialSyncPolicy !== 'paused') {
            ;(workspace as RealWorkspace).setSyncPolicy(initialSyncPolicy)
        }
        this.trackWorkspace(workspace)
        this.syncLoadedWorkspaceMetadata(params.blobId, nextEntry)
        this.setActiveWorkspace(workspace)
        this.ensureWsClient(params.apiBaseUrl)
        this.subscribeWorkspaceNotifications(params.blobId)
        if (!wasKnown || this.workspaceEntryChanged(currentEntry, nextEntry)) {
            this.saveRegistry()
            this.events.emit('registry:changed', undefined)
        }
        return workspace
    }

    async activateWorkspace(blobId: string): Promise<Workspace> {
        const entry = this.registry.get(blobId)
        if (!entry) throw new Error(`Workspace ${blobId} not found`)

        if (this.activeWorkspace?.blobId === blobId) {
            if (this.shouldReloadWorkspace(this.activeWorkspace, entry)) {
                this.unloadWorkspace(blobId, this.activeWorkspace)
            } else {
                this.syncLoadedWorkspaceMetadata(blobId, entry)
                return this.activeWorkspace
            }
        }

        if (this.activeWorkspace?.blobId && this.activeWorkspace.blobId !== blobId) {
            this.suspendWorkspace(this.activeWorkspace.blobId)
        }

        const loaded = this.workspaces.get(blobId)
        if (loaded) {
            if (this.shouldReloadWorkspace(loaded, entry)) {
                this.unloadWorkspace(blobId, loaded)
            } else {
                this.syncLoadedWorkspaceMetadata(blobId, entry)
                this.setActiveWorkspace(loaded)
                return loaded
            }
        }

        return this.openWorkspace({
            apiBaseUrl: entry.apiBaseUrl,
            blobId: entry.blobId,
            capability: entry.capability,
            canWrite: entry.canWrite,
        })
    }

    suspendWorkspace(blobId: string): void {
        const ws = this.workspaces.get(blobId)
        if (!ws) return

        this.setWorkspaceMode(ws, 'suspended')
        this.markSuspended(blobId)
        if (this.activeWorkspace?.blobId === blobId) this.activeWorkspace = null

        while (this.suspendedWorkspaceIds.length > 3) {
            const evictedId = this.suspendedWorkspaceIds.shift()
            if (!evictedId || evictedId === this.activeWorkspace?.blobId) continue
            const evictedWorkspace = this.workspaces.get(evictedId)
            if (!evictedWorkspace) continue
            const apiBaseUrl = this.getWorkspaceApiBaseUrl(evictedId)
            this.unsubscribeWorkspaceNotifications(evictedId)
            evictedWorkspace.close()
            this.workspaces.delete(evictedId)
            this.syncStateUnsubscribes.get(evictedId)?.()
            this.syncStateUnsubscribes.delete(evictedId)
            this.disposeWsClientIfUnused(apiBaseUrl)
        }
    }

    getActiveWorkspace(): Workspace | null { return this.activeWorkspace }

    getSuspendedWorkspaces(): WorkspaceInfo[] {
        return this.suspendedWorkspaceIds
            .map(blobId => this.registry.get(blobId))
            .filter((entry): entry is WorkspaceEntry => Boolean(entry))
            .map(entry => ({
                blobId: entry.blobId,
                label: entry.label,
                emoji: entry.emoji,
                state: { syncing: false, online: true, connectivity: 'connected', synced: true, lastSyncAt: 0, pendingChanges: 0, syncLog: [] },
            }))
    }

    hasIdentity(): boolean { return this.identity.hasIdentity() }
    getIdentity() { return this.identity.getIdentity() }
    async createIdentity(params?: { name?: string; emoji?: string; pin?: string; webAuthnOnly?: boolean }): Promise<UserIdentity | null> {
        const created = await this.identity.createIdentity(params)
        this.registry.clear()
        this.mcpConnections.clear()
        this.loadRegistry()
        // Await first sync — new identity has no remote data, but this bootstraps the blob
        await this.initRegistrySync()
        this.events.emit('registry:changed', undefined)
        this.events.emit('identity:changed', undefined)
        getCrossTabChannel().broadcast({ type: 'identity-changed' })
        return created
    }
    async importIdentity(payload: string) {
        const imported = await this.identity.importIdentity(payload)
        this.registry.clear()
        this.mcpConnections.clear()
        this.loadRegistry()
        // Await first sync — pulls remote workspaces before UI reacts to identity:changed
        await this.initRegistrySync()
        this.events.emit('registry:changed', undefined)
        this.events.emit('identity:changed', undefined)
        getCrossTabChannel().broadcast({ type: 'identity-changed' })
        return imported
    }
    async exportIdentity() {
        return this.identity.exportIdentity()
    }

    logoutIdentity(): void {
        this.identity.lock()
        this.teardownSession()
        this.events.emit('identity:changed', undefined)
        getCrossTabChannel().broadcast({ type: 'identity-changed' })
    }

    async deleteIdentity(): Promise<void> {
        // Capture kid before vault deletion so we know which databases to drop
        const kid = this.identity.getIdentity()?.kid

        this.teardownSession()
        await this.identity.deleteVault()

        // Final cleanup of orphaned identity data (caches that survived teardown)
        if (kid) {
            try { localStorage.removeItem(`sb:registry:${kid}`) } catch { /* ignore */ }
            try { localStorage.removeItem(`sb:registry:${kid}:tombstoned`) } catch { /* ignore */ }
            if (typeof indexedDB !== 'undefined') {
                indexedDB.deleteDatabase(`secureblob-${kid}`)
            }
        }

        this.events.emit('identity:changed', undefined)
        getCrossTabChannel().broadcast({ type: 'identity-changed' })
    }

    /** §4.2: Complete session teardown — shared by logout, delete, and cross-tab loss */
    private teardownSession(): void {
        for (const blobId of this.workspaces.keys()) {
            this.unsubscribeWorkspaceNotifications(blobId)
        }
        this.wsClients.forEach(client => client.dispose())
        this.wsClients.clear()
        // Close all active/suspended workspaces
        this.workspaces.forEach(ws => ws.close())
        this.workspaces.clear()
        this.syncStateUnsubscribes.forEach(unsub => unsub())
        this.syncStateUnsubscribes.clear()
        this.suspendedWorkspaceIds.length = 0
        this.activeWorkspace = null

        // Clear in-memory registry so stale entries don't bleed across identities
        this.registry.clear()
        this.mcpConnections.clear()
        this.registryPreferences = {}

        // Tear down registry sync lifecycle (polling, debounce timer, instance)
        this.stopRegistryPolling()
        if (this.registrySyncTimer) {
            clearTimeout(this.registrySyncTimer)
            this.registrySyncTimer = null
        }
        this.registrySync = null

        // Clear all session keys (active workspace, guest caps, pending workspace)
        try {
            clearActiveWorkspace(sessionStorage)
            sessionStorage.removeItem('sb:guest-active')
            sessionStorage.removeItem('sb:pending-workspace')
            Object.keys(sessionStorage)
                .filter(k => k.startsWith('sb:guest-cap:'))
                .forEach(k => sessionStorage.removeItem(k))
        } catch { /* SSR-safe */ }
    }

    async tryAutoUnlock(): Promise<boolean> {
        // Idempotent: if already unlocked and initialized, don't re-run
        // destructive work (registry.clear, event emissions, initRegistrySync).
        // React StrictMode dev double-mount calls this twice on the same
        // manager instance (useMemo keeps it stable across re-renders).
        if (this.identity.isUnlocked()) return true

        const ok = await this.identity.tryAutoUnlock()
        if (ok) {
            this.registry.clear()
            this.mcpConnections.clear()
            this.loadRegistry()
            this.events.emit('registry:changed', undefined)
            this.events.emit('identity:changed', undefined)
            // Auto-unlock has local data — background sync is fine
            void this.initRegistrySync()
        }
        return ok
    }

    async unlockWithWebAuthn(): Promise<boolean> {
        const ok = await this.identity.unlockWithWebAuthn()
        if (ok) {
            this.registry.clear()
            this.mcpConnections.clear()
            this.loadRegistry()
            this.events.emit('registry:changed', undefined)
            this.events.emit('identity:changed', undefined)
            // WebAuthn unlock has local data — background sync is fine
            void this.initRegistrySync()
        }
        return ok
    }

    async updateProfile(profile: { name?: string; emoji?: string }): Promise<void> {
        await this.identity.updateProfile(profile)
        const identity = this.identity.getIdentity()
        if (identity) {
            const owner = { primaryKid: identity.kid, displayName: identity.name, emoji: identity.emoji }
            await Promise.all(
                Array.from(this.mcpConnections.values())
                    .filter(connection => connection.ownerKid === identity.kid)
                    .map(async connection => {
                        try {
                            await refreshConfigBlobOwner(
                                this.getMcpApiBaseUrl(connection),
                                connection.configBlobId,
                                connection.readKey,
                                getConfigReaderSeed(connection),
                                connection.writerSeed,
                                owner,
                            )
                        } catch (error) {
                            console.warn(`[MCP] Failed to refresh owner for ${connection.configBlobId.slice(0, 8)}:`, error)
                        }
                    }),
            )
        }
        this.events.emit('identity:changed', undefined)
    }

    async recoverFromServer(): Promise<boolean> {
        const ok = await this.identity.recoverFromServer()
        if (ok) {
            this.registry.clear()
            this.mcpConnections.clear()
            this.loadRegistry()
            // Await first sync — recovery has no local data, must pull from remote first
            await this.initRegistrySync()
            this.events.emit('registry:changed', undefined)
            this.events.emit('identity:changed', undefined)
        }
        return ok
    }

    // ─── Trusted Device delegation (§05 §10, §148) ──────────────────────────

    async trustDevice(label?: string): Promise<{ deviceId: string }> {
        return this.identity.trustDevice(label)
    }

    async getTrustedDevices() {
        return this.identity.getTrustedDevices()
    }

    async revokeTrustedDevice(deviceId: string): Promise<void> {
        await this.identity.revokeTrustedDevice(deviceId)
    }

    async revokeAllTrustedDevices(): Promise<void> {
        await this.identity.revokeAllTrustedDevices()
    }

    async isCurrentDeviceTrusted(): Promise<boolean> {
        return this.identity.isCurrentDeviceTrusted()
    }

    getRegistry(): WorkspaceEntry[] { return [...this.registry.values()] }
    addToRegistry(entry: WorkspaceEntry): void {
        // Refuse to re-add workspaces that were tombstoned (permanently deleted).
        // This prevents ensureHomeWorkspace() from resurrecting deleted Home workspaces.
        if (this.getTombstonedBlobIds().has(entry.blobId)) {
            console.info(`[Registry] Skipping tombstoned workspace ${entry.blobId}`)
            return
        }
        this.registry.set(entry.blobId, entry)
        this.syncLoadedWorkspaceMetadata(entry.blobId, entry)
        this.saveRegistry()
        this.events.emit('registry:changed', undefined)
    }
    leaveWorkspace(blobId: string): void {
        // Remove from registry WITHOUT tombstoning — user can rejoin via share link.
        const ws = this.workspaces.get(blobId)
        const apiBaseUrl = this.getWorkspaceApiBaseUrl(blobId)
        const leavingActiveWorkspace = this.activeWorkspace?.blobId === blobId

        // Close and remove workspace if loaded
        if (ws) {
            this.unsubscribeWorkspaceNotifications(blobId)
            ws.close()
            this.workspaces.delete(blobId)
            this.syncStateUnsubscribes.get(blobId)?.()
            this.syncStateUnsubscribes.delete(blobId)
            this.disposeWsClientIfUnused(apiBaseUrl)
        }

        // Remove from registry (no tombstone — "leave" is not "delete")
        this.registry.delete(blobId)
        this.saveRegistry()

        // Clear active workspace ref if leaving the active one
        if (leavingActiveWorkspace) {
            this.activeWorkspace = null
            try { clearActiveWorkspace(sessionStorage) } catch { /* SSR-safe */ }
            this.events.emit('workspace:switched', null)
        }

        this.events.emit('registry:changed', undefined)
    }
    removeFromRegistry(blobId: string): void {
        this.registry.delete(blobId)
        this.addToTombstoneList(blobId)
        this.saveRegistry()
        this.events.emit('registry:changed', undefined)
    }
    updateRegistryEntry(blobId: string, updates: Partial<Pick<WorkspaceEntry, 'label' | 'emoji' | 'notifications'>>): void {
        const entry = this.registry.get(blobId)
        if (entry) {
            // §5.10: Bump lastOpenedAt on metadata mutation for LWW merge correctness.
            // Without this, a stale debounce-push could win merge against the renamed copy.
            entry.lastOpenedAt = new Date().toISOString()
            const updated = { ...entry, ...updates }
            this.registry.set(blobId, updated)
            this.syncLoadedWorkspaceMetadata(blobId, updated)
            this.saveRegistry()
            this.events.emit('registry:changed', undefined)
        }
    }

    async deleteWorkspace(blobId: string): Promise<void> {
        const ws = this.workspaces.get(blobId) as RealWorkspace | undefined
        const apiBaseUrl = this.getWorkspaceApiBaseUrl(blobId)
        if (ws && ws.canWrite) {
            try {
                await ws.commitTombstone()
            } catch (e) {
                console.warn('Tombstone commit failed, removing locally:', e)
            }
        }
        // Close and remove workspace
        if (ws) {
            this.unsubscribeWorkspaceNotifications(blobId)
            ws.close()
            this.workspaces.delete(blobId)
            this.syncStateUnsubscribes.get(blobId)?.()
            this.syncStateUnsubscribes.delete(blobId)
            this.disposeWsClientIfUnused(apiBaseUrl)
        }
        if (this.activeWorkspace?.blobId === blobId) {
            this.activeWorkspace = null
            // §4.1: Clear so F5 doesn't try to reopen a deleted workspace
            try { clearActiveWorkspace(sessionStorage) } catch { /* SSR-safe */ }
            this.events.emit('workspace:switched', null)
        }
        // Remove from registry and add to tombstone list
        this.removeFromRegistry(blobId)
        this.addToTombstoneList(blobId)
    }

    isTombstoned(blobId: string): boolean {
        return this.getTombstonedBlobIds().has(blobId)
    }

    removeTombstone(blobId: string): void {
        const set = this.getTombstonedBlobIds()
        if (set.delete(blobId)) {
            this.saveTombstones(set)
            console.info(`[Registry] Removed tombstone for ${blobId}`)
        }
    }

    // MCP Connections (registry-backed, spec §07 §3.4)
    getMcpConnections(): McpConnectionEntry[] { return [...this.mcpConnections.values()] }
    addMcpConnection(entry: McpConnectionEntry): void {
        this.removeTombstone(entry.configBlobId)
        this.mcpConnections.set(entry.configBlobId, entry)
        this.saveRegistry()
        this.events.emit('registry:changed', undefined)
    }
    removeMcpConnection(configBlobId: string): void {
        this.mcpConnections.delete(configBlobId)
        this.addToTombstoneList(configBlobId)
        this.saveRegistry()
        this.events.emit('registry:changed', undefined)
    }
    updateMcpConnection(configBlobId: string, updates: Partial<McpConnectionEntry>): void {
        const entry = this.mcpConnections.get(configBlobId)
        if (entry) {
            this.mcpConnections.set(configBlobId, { ...entry, ...updates })
            this.saveRegistry()
            this.events.emit('registry:changed', undefined)
        }
    }

    lookupKid(kid: string): ProfileEntry | null {
        const identity = this.identity.getIdentity()
        if (!identity) return null
        // Match identity kid (legacy) OR derived writer kids from any active workspace
        if (identity.kid === kid) return identity
        for (const ws of this.workspaces.values()) {
            if (ws instanceof RealWorkspace && ws.derivedKid === kid) return identity
        }
        return null
    }
    on<E extends EngineManagerEventType>(event: E, fn: (payload: EngineManagerEventPayload[E]) => void): () => void { return this.events.on(event, fn) }
    /** §7.6.1 Unified manual sync — sync workspace + registry + MCP config-blobs in parallel */
    async syncAllNow(): Promise<void> {
        const tasks: Promise<void>[] = []

        // 1. Active workspace sync
        if (this.activeWorkspace) {
            tasks.push(
                this.activeWorkspace.syncNow()
                    .catch(err => console.warn('syncAllNow: workspace sync failed', err))
            )
        }

        // 2. Registry pull + merge + push
        tasks.push(
            this.syncRegistryFromRemote()
                .catch(err => console.warn('syncAllNow: registry sync failed', err))
        )

        // 3. MCP config-blob verification (runs after registry sync finishes)
        // Already triggered inside syncRegistryFromRemote on success,
        // but we also run it explicitly in case registry sync was a no-op
        tasks.push(
            this.verifyMcpConfigBlobs()
                .catch(err => console.warn('syncAllNow: MCP verify failed', err))
        )

        await Promise.allSettled(tasks)
    }

    dispose(): void {
        // Cleanup registry sync lifecycle handles
        this.stopRegistryPolling()
        if (this.registrySyncTimer) {
            clearTimeout(this.registrySyncTimer)
            this.registrySyncTimer = null
        }
        if (typeof document !== 'undefined' && this.handleVisibilityChange) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange)
            this.handleVisibilityChange = null
        }
        if (typeof window !== 'undefined') {
            if (this.handleOnlineForRegistry) {
                window.removeEventListener('online', this.handleOnlineForRegistry)
                this.handleOnlineForRegistry = null
            }
            if (this.handleOfflineForRegistry) {
                window.removeEventListener('offline', this.handleOfflineForRegistry)
                this.handleOfflineForRegistry = null
            }
            if (this.handleBeforeUnload) {
                window.removeEventListener('beforeunload', this.handleBeforeUnload)
                this.handleBeforeUnload = null
            }
        }
        // Cleanup workspaces and WS client
        for (const blobId of this.workspaces.keys()) {
            this.unsubscribeWorkspaceNotifications(blobId)
        }
        this.wsClients.forEach(client => client.dispose())
        this.wsClients.clear()
        this.syncStateUnsubscribes.forEach(unsubscribe => unsubscribe())
        this.syncStateUnsubscribes.clear()
        this.workspaces.forEach(workspace => workspace.close())
        this.workspaces.clear()
        this.suspendedWorkspaceIds.length = 0
    }

    private ensureWsClient(apiBaseUrl: string): WsNotificationClient {
        const existing = this.wsClients.get(apiBaseUrl)
        if (existing) return existing

        const wsClient = new WsNotificationClient(apiBaseUrl, {
            onHeadChanged: (blobId, head, lane) => {
                if (lane != null) {
                    const ws = this.workspaces.get(blobId)
                    if (ws instanceof RealWorkspace) {
                        ws.triggerSqlite3MtSync(this.buildSqlite3MtHeadBlobId(blobId, lane))
                    }
                    return
                }

                const workspaceBlobId = this.getSqlite3MtWorkspaceBlobId(blobId)
                if (workspaceBlobId) {
                    const ws = this.workspaces.get(workspaceBlobId)
                    if (ws instanceof RealWorkspace) {
                        ws.triggerSqlite3MtSync(blobId)
                    }
                    return
                }

                // Standard workspace head change
                const ws = this.workspaces.get(blobId)
                if (ws instanceof RealWorkspace) {
                    // Self-echo filter: skip sync for our own pipeline-pushed commits
                    if (ws.isOwnPipelineHead(head)) return
                    ws.triggerSync()
                }
            },
            onConnectionChange: (connected) => {
                // Scope notification mode to workspaces on this transport origin.
                for (const ws of this.workspaces.values()) {
                    if (
                        ws instanceof RealWorkspace &&
                        this.getWorkspaceApiBaseUrl(ws.blobId) === apiBaseUrl
                    ) {
                        ws.setNotificationSource(connected ? 'websocket' : 'polling')
                    }
                }
            },
        }, (blobId) => this.resolveWorkspaceAuthProvider(blobId))
        this.wsClients.set(apiBaseUrl, wsClient)
        wsClient.connect()
        return wsClient
    }

    private trackWorkspace(workspace: Workspace): void {
        this.workspaces.set(workspace.blobId, workspace)
        this.syncStateUnsubscribes.get(workspace.blobId)?.()
        let unsubscribeSqlite3MtHeads = () => {}
        if (workspace instanceof RealWorkspace) {
            for (const headBlobId of workspace.getSqlite3MtHeadBlobIds()) {
                this.subscribeSqlite3MtHeadBlob(workspace.blobId, headBlobId)
            }
            const unsubscribeRegistered = workspace.onSqlite3MtHeadRegistered(headBlobId => {
                this.subscribeSqlite3MtHeadBlob(workspace.blobId, headBlobId)
            })
            const unsubscribeUnregistered = workspace.onSqlite3MtHeadUnregistered(headBlobId => {
                this.unsubscribeSqlite3MtHeadBlob(workspace.blobId, headBlobId)
            })
            unsubscribeSqlite3MtHeads = () => {
                unsubscribeRegistered()
                unsubscribeUnregistered()
            }
        }

        const unsubscribeState = workspace.on('state:changed', state => {
            this.events.emit('workspace:sync-status', { blobId: workspace.blobId, state })

            // BLOB_SOFT_DELETED lifecycle: workspace permanently deleted server-side.
            // Forward as workspace:deleted so UI shows the overlay, then clean up.
            if (state.tombstoned) {
                const apiBaseUrl = this.getWorkspaceApiBaseUrl(workspace.blobId)
                this.events.emit('workspace:deleted', { blobId: workspace.blobId })
                this.unsubscribeWorkspaceNotifications(workspace.blobId)
                this.workspaces.delete(workspace.blobId)
                this.syncStateUnsubscribes.get(workspace.blobId)?.()
                this.syncStateUnsubscribes.delete(workspace.blobId)
                this.disposeWsClientIfUnused(apiBaseUrl)
                // §7.7: Auto-remove from registry so workspace doesn't reappear on reload
                this.removeFromRegistry(workspace.blobId)
                // Persist tombstone so deterministic workspaces (Home) can't be re-added
                this.addToTombstoneList(workspace.blobId)
                // Clear active workspace ref & sessionStorage (prevents F5 reopening deleted ws)
                if (this.activeWorkspace?.blobId === workspace.blobId) {
                    this.activeWorkspace = null
                    try { clearActiveWorkspace(sessionStorage) } catch { /* SSR-safe */ }
                }
            }
        })
        this.syncStateUnsubscribes.set(workspace.blobId, () => {
            unsubscribeSqlite3MtHeads()
            unsubscribeState()
        })
        this.events.emit('workspace:sync-status', { blobId: workspace.blobId, state: workspace.state })
    }

    private setActiveWorkspace(workspace: Workspace): void {
        console.log(`[EngineManager] setActiveWorkspace blob=${workspace.blobId.slice(0, 12)}`)
        this.activeWorkspace = workspace
        const suspendedIndex = this.suspendedWorkspaceIds.indexOf(workspace.blobId)
        if (suspendedIndex >= 0) this.suspendedWorkspaceIds.splice(suspendedIndex, 1)
        this.setWorkspaceMode(workspace, 'active')
        // §4.1: Persist active workspace so F5 restores it (single chokepoint)
        try { saveActiveWorkspace(sessionStorage, workspace.blobId) } catch { /* SSR-safe */ }
        // §4.1 step 7: Update lastOpenedAt for registry fallback (new tab / cross-device)
        const entry = this.registry.get(workspace.blobId)
        if (entry) {
            (entry as RegistryWorkspaceEntry).lastOpenedAt = new Date().toISOString()
            this.saveRegistry()
        }
        this.events.emit('workspace:switched', workspace)
    }

    private markSuspended(blobId: string): void {
        const currentIndex = this.suspendedWorkspaceIds.indexOf(blobId)
        if (currentIndex >= 0) {
            this.suspendedWorkspaceIds.splice(currentIndex, 1)
        }
        this.suspendedWorkspaceIds.push(blobId)
    }

    private workspaceEntryChanged(
        previous: WorkspaceEntry | undefined,
        next: WorkspaceEntry,
    ): boolean {
        if (!previous) return true
        return previous.capability !== next.capability
            || previous.apiBaseUrl !== next.apiBaseUrl
            || previous.label !== next.label
            || previous.emoji !== next.emoji
            || previous.canWrite !== next.canWrite
            || previous.trustLevel !== next.trustLevel
            || previous.agentSecretsAllowed !== next.agentSecretsAllowed
    }

    private shouldReloadWorkspace(workspace: Workspace, entry: WorkspaceEntry): boolean {
        if (!(workspace instanceof RealWorkspace)) return false
        const current = workspace.getEntrySnapshot()
        return current.capability !== entry.capability
            || current.apiBaseUrl !== entry.apiBaseUrl
            || current.canWrite !== entry.canWrite
    }

    private syncLoadedWorkspaceMetadata(blobId: string, entry: WorkspaceEntry): void {
        const workspace = this.workspaces.get(blobId)
        if (workspace instanceof RealWorkspace) {
            workspace.updateEntryMetadata({
                label: entry.label,
                emoji: entry.emoji,
            })
            const explicitPolicy = getExplicitLocalWorkspaceSyncPolicy(blobId)
            if (explicitPolicy != null) {
                workspace.setSyncPolicy(explicitPolicy)
            }
        }
    }

    private unloadWorkspace(blobId: string, workspace: Workspace): void {
        const apiBaseUrl = this.getWorkspaceApiBaseUrl(blobId)
        this.unsubscribeWorkspaceNotifications(blobId)
        workspace.close()
        this.workspaces.delete(blobId)
        this.syncStateUnsubscribes.get(blobId)?.()
        this.syncStateUnsubscribes.delete(blobId)
        const suspendedIndex = this.suspendedWorkspaceIds.indexOf(blobId)
        if (suspendedIndex >= 0) this.suspendedWorkspaceIds.splice(suspendedIndex, 1)
        if (this.activeWorkspace?.blobId === blobId) this.activeWorkspace = null
        this.disposeWsClientIfUnused(apiBaseUrl)
    }

    private setWorkspaceMode(workspace: Workspace, mode: 'active' | 'suspended'): void {
        if (workspace instanceof RealWorkspace) {
            workspace.setSyncMode(mode)
        }
    }

    private subscribeWorkspaceNotifications(blobId: string): void {
        const apiBaseUrl = this.getWorkspaceApiBaseUrl(blobId)
        if (!apiBaseUrl) return

        const wsClient = this.ensureWsClient(apiBaseUrl)
        const workspace = this.workspaces.get(blobId)
        if (workspace instanceof RealWorkspace) {
            workspace.setNotificationSource(wsClient.isConnected ? 'websocket' : 'polling')
        }
        wsClient.subscribe(blobId)
        for (const headBlobId of this.sqlite3mtHeadSubscriptions.get(blobId) ?? []) {
            const sqliteTarget = this.parseSqlite3MtHeadSubscription(headBlobId)
            if (sqliteTarget) {
                wsClient.subscribe(sqliteTarget.workspaceBlobId, sqliteTarget.lane)
            } else {
                wsClient.subscribe(headBlobId)
            }
        }
    }

    private subscribeSqlite3MtHeadBlob(workspaceBlobId: string, headBlobId: string): void {
        let headBlobIds = this.sqlite3mtHeadSubscriptions.get(workspaceBlobId)
        if (!headBlobIds) {
            headBlobIds = new Set<string>()
            this.sqlite3mtHeadSubscriptions.set(workspaceBlobId, headBlobIds)
        }

        if (headBlobIds.has(headBlobId)) return

        headBlobIds.add(headBlobId)
        const apiBaseUrl = this.getWorkspaceApiBaseUrl(workspaceBlobId)
        if (!apiBaseUrl) return
        const wsClient = this.ensureWsClient(apiBaseUrl)
        const sqliteTarget = this.parseSqlite3MtHeadSubscription(headBlobId)
        if (sqliteTarget) {
            wsClient.subscribe(sqliteTarget.workspaceBlobId, sqliteTarget.lane)
        } else {
            wsClient.subscribe(headBlobId)
        }
    }

    private unsubscribeSqlite3MtHeadBlob(workspaceBlobId: string, headBlobId: string): void {
        const headBlobIds = this.sqlite3mtHeadSubscriptions.get(workspaceBlobId)
        if (!headBlobIds?.delete(headBlobId)) {
            return
        }

        const apiBaseUrl = this.getWorkspaceApiBaseUrl(workspaceBlobId)
        if (apiBaseUrl) {
            const wsClient = this.wsClients.get(apiBaseUrl)
            const sqliteTarget = this.parseSqlite3MtHeadSubscription(headBlobId)
            if (sqliteTarget) {
                wsClient?.unsubscribe(sqliteTarget.workspaceBlobId, sqliteTarget.lane)
            } else {
                wsClient?.unsubscribe(headBlobId)
            }
        }
        if (headBlobIds.size === 0) {
            this.sqlite3mtHeadSubscriptions.delete(workspaceBlobId)
        }
    }

    private unsubscribeWorkspaceNotifications(blobId: string): void {
        const apiBaseUrl = this.getWorkspaceApiBaseUrl(blobId)
        const wsClient = apiBaseUrl ? this.wsClients.get(apiBaseUrl) : null
        wsClient?.unsubscribe(blobId)
        const headBlobIds = this.sqlite3mtHeadSubscriptions.get(blobId)
        this.sqlite3mtHeadSubscriptions.delete(blobId)
        if (!headBlobIds) return

        for (const headBlobId of headBlobIds) {
            const sqliteTarget = this.parseSqlite3MtHeadSubscription(headBlobId)
            if (sqliteTarget) {
                wsClient?.unsubscribe(sqliteTarget.workspaceBlobId, sqliteTarget.lane)
            } else {
                wsClient?.unsubscribe(headBlobId)
            }
        }
    }

    private disposeWsClientIfUnused(apiBaseUrl: string | null): void {
        if (!apiBaseUrl) return

        for (const workspaceId of this.workspaces.keys()) {
            if (this.getWorkspaceApiBaseUrl(workspaceId) === apiBaseUrl) {
                return
            }
        }

        const wsClient = this.wsClients.get(apiBaseUrl)
        if (!wsClient) return

        wsClient.dispose()
        this.wsClients.delete(apiBaseUrl)
    }

    private getWorkspaceApiBaseUrl(blobId: string): string | null {
        return this.registry.get(blobId)?.apiBaseUrl ?? null
    }

    private resolveWorkspaceAuthProvider(blobId: string) {
        const workspace = this.workspaces.get(blobId)
        if (workspace instanceof RealWorkspace) {
            return workspace.getAuthProvider()
        }

        const workspaceBlobId = this.getSqlite3MtWorkspaceBlobId(blobId)
        if (!workspaceBlobId) return undefined

        const carrierWorkspace = this.workspaces.get(workspaceBlobId)
        return carrierWorkspace instanceof RealWorkspace
            ? carrierWorkspace.getAuthProvider()
            : undefined
    }

    private buildSqlite3MtHeadBlobId(blobId: string, lane: string): string {
        return `${blobId}:sqlite3mt:${lane}`
    }

    private parseSqlite3MtHeadSubscription(headBlobId: string): { workspaceBlobId: string; lane: string } | null {
        const marker = ':sqlite3mt:'
        const markerIndex = headBlobId.indexOf(marker)
        if (markerIndex <= 0) return null

        const workspaceBlobId = headBlobId.slice(0, markerIndex)
        const lane = headBlobId.slice(markerIndex + marker.length)
        if (!workspaceBlobId || !lane) return null

        return { workspaceBlobId, lane }
    }

    private getSqlite3MtWorkspaceBlobId(blobId: string): string | null {
        return this.parseSqlite3MtHeadSubscription(blobId)?.workspaceBlobId ?? null
    }
}

export function createRealEngineManager(): EngineManager {
    return new RealEngineManager()
}
